import React, { useState, useEffect } from 'react';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';

import EmailList from './EmailList';
import JiraIssueTable from './JiraIssueTable';
import { functionResponsesRequiringApproval } from '../../utils/constants';
import DynamicJiraIssueTable from './DynamicJiraIssueTable';
import CalendarEventEditor from '../meeting/CalendarEventEditor';
import DynamicTable from './DynamicTable';
import DynamicDetail from './DynamicDetail';
import FileOperationOutput from './FileOperationOutput';
import { useResourceAllocation } from '../../context/ResourceAllocationProvider';

const ToolsResponseUI = ({ tool, conversations }) => {
  const [contentParsed, setContentParsed] = useState(null);
  const [showOutput, setShowOutput] = useState(false);
  const { openRAModal } = useResourceAllocation();

  // A plan is renderable if it declares a display_type (v2) or carries the old
  // is_table_displayable flag (v1, still present on historical messages).
  const plan = tool?.ui_table_plan;
  const hasRenderPlan =
    (plan?.display_type &&
      plan.display_type !== 'none' &&
      plan?.fields?.length) ||
    plan?.is_table_displayable;

  useEffect(() => {
    if (
      (!tool?.functionName ||
        !functionResponsesRequiringApproval.has(tool.functionName)) &&
      !hasRenderPlan
    ) {
      setContentParsed(null);
      return;
    }

    let rawResponseParsed = {};
    try {
      if (tool.display_data) {
        rawResponseParsed = tool.display_data;
      } else {
        rawResponseParsed = JSON.parse(tool.content || '{}');
      }
      setContentParsed(rawResponseParsed?.tool_response || rawResponseParsed);
    } catch (err) {
      setContentParsed({ error: 'Failed to parse tool response.' });
    }
  }, [tool, hasRenderPlan]);

  // Instructions for the model (backend NO_RESULT_CARD_TOOLS); older chats
  // still carry a render plan for it, so skip it here too.
  if (tool?.functionName === 'get_dashboard_guide') return null;

  // **RENDER PATH 0: Resource Allocation Panel**
  if (
    tool.functionName === 'search_resources_for_module' &&
    contentParsed &&
    !contentParsed.error
  ) {
    const ra = contentParsed;
    const skills = ra.initialSkills || ra.skills || '';
    const skillList = skills
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    return (
      <div className="w-full px-2 sm:px-4">
        <div className="mt-1 overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900 shadow-lg">
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <div className="min-w-0 flex-1">
              <div className="mb-1.5 flex items-center gap-2">
                <span className="flex-shrink-0 rounded bg-blue-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-400">
                  Resource search
                </span>
                <span className="truncate text-sm font-semibold text-zinc-100">
                  {ra.moduleName || 'Module'}
                </span>
              </div>
              {ra.projectName && (
                <p className="mb-1.5 text-xs text-zinc-500">{ra.projectName}</p>
              )}
              {skillList.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {skillList.map((s) => (
                    <span
                      key={s}
                      className="rounded border border-zinc-700 bg-zinc-800 px-1.5 py-0.5 text-[11px] text-zinc-400"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => openRAModal(ra)}
              className="flex-shrink-0 rounded-lg bg-[#5652CF] px-3.5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[#4845b0]"
            >
              View matches →
            </button>
          </div>
        </div>
      </div>
    );
  }

  // **RENDER PATH 1: Calendar Event CREATION (ENHANCED)**
  const isSuccessfulCalendarCreation =
    ['create_calendar_event_outlook', 'create_google_calendar_event'].includes(
      tool.functionName,
    ) &&
    contentParsed &&
    !contentParsed.error;

  if (isSuccessfulCalendarCreation) {
    // --- ENHANCED DATA MAPPING ---
    const data = contentParsed;
    const finalEventData = {
      title: data.summary || data.title || data.subject || 'Untitled Event',
      start: data.start?.dateTime || data.start?.time || data.startTime,
      end: data.end?.dateTime || data.end?.time || data.endTime,
      description: data.description || data.body?.content,
      location: data.location,
      attendees: data.attendees || [],
      // Captures the "Join" link for virtual meetings
      meetLink:
        data.hangoutLink ||
        data.conferenceData?.entryPoints?.[0]?.uri ||
        data.onlineMeeting?.joinUrl,
      // Captures the link to view the event in the calendar UI
      eventUrl: data.htmlLink || data.webLink,
    };

    return (
      <div className="sm-pr-6 w-full pl-8 pr-4 sm:pl-12">
        <CalendarEventEditor
          event={finalEventData}
          approvalNeeded={false}
          isReadOnly={true}
        />
      </div>
    );
  }

  // **RENDER PATH 2: Calendar Event UPDATE (ENHANCED)**
  const isSuccessfulCalendarUpdate =
    ['update_calendar_event', 'update_google_calendar_event'].includes(
      tool.functionName,
    ) &&
    contentParsed &&
    !contentParsed.error;

  if (isSuccessfulCalendarUpdate) {
    // --- ENHANCED DATA MAPPING ---
    const data = contentParsed;
    const finalEventData = {
      title: data.summary || data.title || data.subject || 'Event Updated',
      start: data.start?.dateTime || data.start?.time || data.startTime,
      end: data.end?.dateTime || data.end?.time || data.endTime,
      description: data.description || data.body?.content,
      location: data.location,
      attendees: data.attendees || [],
      meetLink:
        data.hangoutLink ||
        data.conferenceData?.entryPoints?.[0]?.uri ||
        data.onlineMeeting?.joinUrl,
      eventUrl: data.htmlLink || data.webLink,
    };

    return (
      <div className="w-full pl-8 pr-4 sm:pl-12 sm:pr-6">
        <CalendarEventEditor
          event={finalEventData}
          approvalNeeded={false}
          isReadOnly={true}
        />
      </div>
    );
  }

  // **RENDER PATH 2.5: create_ticket — scope-gated ticket creation**
  if (
    tool.functionName === 'create_ticket' &&
    contentParsed &&
    !contentParsed.error
  ) {
    const t = contentParsed;
    const isWaiting = t.status === 'CLARIFICATION_REQUIRED';
    return (
      <div className="w-full px-2 sm:px-4">
        <div
          className={`mt-1 overflow-hidden rounded-xl border p-3.5 shadow-lg ${
            isWaiting
              ? 'border-amber-500/30 bg-amber-500/10'
              : 'border-emerald-500/30 bg-emerald-500/10'
          }`}
        >
          <p
            className={`mb-1 text-[10px] font-semibold uppercase tracking-wide ${
              isWaiting ? 'text-amber-400' : 'text-emerald-400'
            }`}
          >
            {isWaiting ? 'Clarification needed' : 'Ticket created'}
          </p>
          <p className="text-sm font-medium text-zinc-100">{t.title}</p>
          {isWaiting ? (
            <p className="mt-1 text-xs text-zinc-400">
              {t.classification?.reason ||
                'Needs more detail before this can become a real ticket. Check the Tickets tab to add a comment.'}
            </p>
          ) : (
            <p className="mt-1 text-xs text-zinc-400">
              {t.jira_issue_key}
              {t.jira_browse_url && (
                <a
                  href={t.jira_browse_url}
                  target="_blank"
                  rel="noreferrer"
                  className="ml-2 underline"
                >
                  Open in Jira
                </a>
              )}
            </p>
          )}
        </div>
      </div>
    );
  }

  // **RENDER PATH 3: Fallback for all other tools**
  if (!contentParsed || contentParsed.error) {
    return null;
  }

  return (
    <>
      {['search_emails_on_gmail', 'list_inbox_emails_on_gmail'].includes(
        tool.functionName,
      ) ? (
        <EmailList emailList={contentParsed.emails || []} />
      ) : ['list_messages_outlook'].includes(tool.functionName) ? (
        <EmailList emailList={contentParsed.emails || []} isOutlook={true} />
      ) : ['search_issues_in_JIRA_independent_of_Agile'].includes(
          tool.functionName,
        ) ? (
        <JiraIssueTable issues={contentParsed} />
      ) : ['advanced_jira_issue_search'].includes(tool.functionName) ? (
        <DynamicJiraIssueTable
          issues={contentParsed.issues || contentParsed.query_result}
        />
      ) : plan?.display_type === 'detail' ? (
        <DynamicDetail plan={plan} data={contentParsed} uiView={tool.ui_view} />
      ) : hasRenderPlan ? (
        <DynamicTable plan={plan} data={contentParsed} uiView={tool.ui_view} />
      ) : null}

      {/* Special case: performCRUDOperations... */}
      {tool.functionName ===
        'performCRUDOperationsOnUserFilesAndGenerateDataVisualizations' &&
      contentParsed['Standard Output'] ? (
        <div className="mt-0">
          <button
            className="flex items-center rounded-md bg-transparent px-2 py-1 hover:bg-zinc-800"
            onClick={() => setShowOutput((prev) => !prev)}
          >
            {showOutput ? 'Analysis' : 'Analysis'}
            {showOutput ? (
              <FiChevronUp className="ml-2 h-5 w-5" />
            ) : (
              <FiChevronDown className="ml-2 h-5 w-5" />
            )}
          </button>
          {showOutput && (
            <div className="ml-1 mt-0 rounded-xl bg-transparent text-sm">
              <div className="mt-0">
                <FileOperationOutput
                  output={contentParsed['Standard Output']}
                />
              </div>
            </div>
          )}
        </div>
      ) : null}

      {tool.functionName !==
        'performCRUDOperationsOnUserFilesAndGenerateDataVisualizations' && (
        <div className="whitespace-pre-wrap p-1 font-mono text-sm ">
          {contentParsed['Standard Output']}
        </div>
      )}
    </>
  );
};

export default ToolsResponseUI;
